import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill, ArrowLeft, ArrowRight, Activity, CheckCircle, CreditCard, Loader2, Info, Crown, Repeat, ShoppingBag, Tag, Sparkles, X, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import TermsAcknowledgmentCard from "@/components/TermsAcknowledgmentCard";
import TierCelebration from "@/components/TierCelebration";
import IdVerificationCard from "@/components/IdVerificationCard";
import IdGateWhyCard from "@/components/IdGateWhyCard";
import { ShieldAlert } from "lucide-react";
import ConsentFlowModal from "@/components/ConsentFlowModal";
import PublicFooter from "@/components/PublicFooter";
import MedicationInfoModal from "@/components/MedicationInfoModal";
import SquareSubscribeModal from "@/components/SquareSubscribeModal";
import VialIllustration from "@/components/VialIllustration";
import ShippingCoverageBadge from "@/components/ShippingCoverageBadge";
import RateLockBadge from "@/components/RateLockBadge";
import IntuitCheckoutModal from "@/components/IntuitCheckoutModal";
import { intuitCtxFromCheckoutError } from "@/lib/intuitCheckout";

/** Map base medication name → vial illustration variant. Falls back to the
 *  violet "semaglutide" palette so new meds still get a presentable card. */
function vialVariantFor(baseName) {
  const n = (baseName || "").toLowerCase();
  if (n.includes("tirzepatide")) return "tirzepatide";
  return "semaglutide";
}

const TIER_INFO = {
  one_time: {
    label: "One-Time Purchase",
    icon: ShoppingBag,
    desc: "Single 30-day supply. Higher per-unit cost but no recurring commitment.",
    detail: "You'll receive a one-time shipment. A new prescription is required for each subsequent order. To reorder, you'll need to submit a new refill request through your dashboard.",
    badge: null,
    color: "border-slate-200 hover:border-purple-200",
    activeColor: "border-purple-500 bg-purple-50",
  },
  solo_subscribe: {
    label: "1-Month Subscribe — 1-Vial Auto-Renew",
    icon: Repeat,
    desc: "Auto-reorders every 28 days. 1 vial per shipment · $4 off the standard Monthly price every cycle.",
    detail: "You'll receive a 28-day (1-vial) supply automatically every month, billed at the Monthly price minus $4. A fresh provider review happens before each cycle. Skip, pause, or cancel anytime from your dashboard.",
    badge: "SAVE $4",
    badgeColor: "bg-sky-500",
    color: "border-slate-200 hover:border-sky-200",
    activeColor: "border-sky-500 bg-sky-50",
  },
  royal_90: {
    label: "3-Month Subscribe — 3-Month Supply",
    icon: Crown,
    desc: "Auto-reorders every 3 months (84 days). 3 vials shipped together with guaranteed overnight shipping — best per-month price.",
    detail: "Our best value! You'll receive an 84-day (3-month) supply — 3 vials shipped at once, delivered via guaranteed overnight shipping, at the lowest per-month price. Each vial can be a different strength as prescribed by your provider. Billed automatically every 84 days (3 months) as a recurring subscription. A fresh provider review happens before each new ship cycle.",
    badge: "BEST VALUE",
    badgeColor: "bg-purple-600",
    color: "border-slate-200 hover:border-purple-200",
    activeColor: "border-purple-500 bg-purple-50",
  },
};

function InfoBubble({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Info className="w-4 h-4 text-slate-400 cursor-help inline" />
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-slate-900 text-white text-xs rounded-xl p-3 shadow-lg leading-relaxed">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-slate-900" />
        </div>
      )}
    </span>
  );
}

export default function MedicationSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const questionnaireId = location.state?.questionnaireId;
  const [medications, setMedications] = useState([]);
  const [selectedMed, setSelectedMed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myQuestionnaires, setMyQuestionnaires] = useState([]);
  const [chosenQId, setChosenQId] = useState(questionnaireId || "");
  const [paymentType, setPaymentType] = useState("one_time");
  const [royalBurstKey, setRoyalBurstKey] = useState(0);
  const [bundleBurstKey, setBundleBurstKey] = useState(0);
  const [celebrationLabels, setCelebrationLabels] = useState({ royal: "ROYAL!", bundle: "SAVE 20%!" });
  const [idStatus, setIdStatus] = useState(null);   // { has_id_card, has_video, verified }
  const [idStatusLoaded, setIdStatusLoaded] = useState(false);
  const [subDiscount, setSubDiscount] = useState(10);
  const [royalDiscount] = useState(20);
  const [showTierDetail, setShowTierDetail] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingPayAfterConsent, setPendingPayAfterConsent] = useState(false);
  const [infoMed, setInfoMed] = useState(null);
  const [duplicateMedInfo, setDuplicateMedInfo] = useState(null);
  // Authoritative per-drug tier prices from /api/pricing (same source of
  // truth the Pricing tab + mobile app read). Keyed by drug_slug so we
  // don't have to guess slug ↔ medication.id mapping on the fly.
  const [pricingTiers, setPricingTiers] = useState({});
  // Patient's saved shipping state — drives the inline ShippingCoverageBadge.
  // Loaded once from /auth/me; the badge component itself polls the
  // pharmacy coverage endpoint on a 30 s interval so live admin changes
  // propagate to the patient's checkout view automatically.
  const [patientState, setPatientState] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [patientProfile, setPatientProfile] = useState({});
  const [subscribeCtx, setSubscribeCtx] = useState(null);
  const [intuitCtx, setIntuitCtx] = useState(null);
  // Resubmit-after-denial deep-link state. Set on mount from URL params
  // (?resubmit=<order_id>&med=<medication_id>&dose=<requested_strength>&reason=<deny_reason>)
  // and shown as a banner so the patient knows we pre-filled their selections.
  const [resubmitContext, setResubmitContext] = useState(null);
  // Denial credit eligibility — fetched once we know the resubmit order ID,
  // displayed as a green "−$25.00" line on the checkout summary, and passed
  // to the backend on checkout so the deduction is applied server-side.
  const [denialCredit, setDenialCredit] = useState(null);
  // Lifted "can we actually ship to this person" verdict; the Pay button
  // gates on this so a patient can't even attempt to submit an order that
  // would 409 at the prescriber's eRx send step.
  const [shippingUnsupported, setShippingUnsupported] = useState(false);

  // Parse the resubmit deep-link once on mount + pre-select medication.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const resubmit = params.get("resubmit");
    if (!resubmit) return;
    setResubmitContext({
      original_order_id: resubmit,
      medication_id: params.get("med") || "",
      requested_dose: params.get("dose") || "",
      reason: params.get("reason") || "",
    });
  }, [location.search]);

  // Once medications load, pre-select the one from the resubmit deep-link.
  useEffect(() => {
    if (!resubmitContext?.medication_id || !medications.length || selectedMed) return;
    const m = medications.find((x) => x.id === resubmitContext.medication_id);
    if (m) setSelectedMed(m);
  }, [resubmitContext, medications, selectedMed]);

  // Fetch the admin-editable tier-celebration text overlays. Falls back
  // to the hard-coded defaults if the endpoint is unreachable.
  useEffect(() => {
    API.get("/celebration-labels")
      .then(({ data }) => setCelebrationLabels({
        royal: data.royal || "ROYAL!",
        bundle: data.bundle || "SAVE 20%!",
      }))
      .catch(() => { /* keep defaults */ });
  }, []);

  // Identity-proofing gate — patients MUST upload their ID + record the
  // video attestation before we let them pick a medication or hit pay.
  // The same check is enforced server-side at POST /api/orders (412).
  useEffect(() => {
    let alive = true;
    API.get("/me/id-verification/status")
      .then(({ data }) => {
        if (!alive) return;
        setIdStatus(data);
        setIdStatusLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        // Non-patient roles get 403 — they shouldn't be on this page anyway.
        setIdStatus({ verified: false, verification_required: true });
        setIdStatusLoaded(true);
      });
    return () => { alive = false; };
  }, []);

  // Patient is ready to move forward as soon as they've UPLOADED an ID
  // (has_id_card) — the doctor performs the final verification during
  // eRx submission, so a "pending review" upload no longer blocks the
  // checkout funnel. Only patients who haven't uploaded anything yet
  // see the gate.
  const idReady = !!(
    idStatus &&
    (!idStatus.verification_required || idStatus.verified || idStatus.has_id_card)
  );

  // Fetch the denial-credit eligibility for the resubmit order. Backend
  // re-validates this exact same logic at checkout time, so even a tampered
  // request can't claim a credit they don't qualify for — this is purely
  // for UI display.
  useEffect(() => {
    if (!resubmitContext?.original_order_id) {
      setDenialCredit(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/orders/denial-credit/${resubmitContext.original_order_id}`);
        if (!cancelled) setDenialCredit(data);
      } catch {
        if (!cancelled) setDenialCredit({ eligible: false, reason: "fetch_failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [resubmitContext]);

  useEffect(() => {
    API.get("/auth/me")
      .then(({ data }) => {
        setPatientState((data?.address_state || "").toUpperCase());
        setPatientEmail((data?.email || ""));
        setPatientProfile({
          given_name: data?.first_name || data?.given_name || "",
          family_name: data?.last_name || data?.family_name || "",
          email: data?.email || "",
          postal_code: data?.address_zip || data?.zip || data?.postal_code || "",
        });
      })
      .catch(() => {});
  }, []);

  /**
   * Defensive dedup: backend already returns one row per medication, but in
   * case a future content edit splits "Semaglutide" / "Semaglutide with B12"
   * into two rows we'd double-list the catalog.  Normalise by base name so
   * the picker always shows one card per active ingredient.
   */
  const uniqueMeds = useMemo(() => {
    const seen = new Map();
    for (const m of medications) {
      const base = (m.name || "").split(/\s+with\s+/i)[0].trim().toLowerCase();
      if (!base || seen.has(base)) continue;
      seen.set(base, m);
    }
    return Array.from(seen.values());
  }, [medications]);
  // Clipboard-detected butterfly code — populated only when the visitor
  // copied a `BFLY-XXXX-XXXX` from the easter-egg popup. Lets us offer
  // one-tap apply instead of forcing them to remember + re-type.
  const [clipboardBfly, setClipboardBfly] = useState("");
  const [bflyDismissed, setBflyDismissed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Computed: show butterfly pill only when clipboard has a BFLY code and user hasn't dismissed
  const showBflyPill = clipboardBfly && !bflyDismissed;

  // Apply butterfly code from clipboard
  const applyBflyFromClipboard = async () => {
    if (!clipboardBfly) return;
    setRedeeming(true);
    try {
      await API.post("/coupons/redeem", { code: clipboardBfly });
      toast.success(`Butterfly code ${clipboardBfly} applied!`);
      setClipboardBfly("");
      setBflyDismissed(true);
      loadDiscounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  const loadDiscounts = useCallback(async () => {
    try {
      const { data } = await API.get("/discounts/check");
      setDiscountInfo(data);
    } catch { void 0; }
  }, []);

  // Live, itemized breakdown — base × each code × credit × final. Shown
  // in the Order Summary so patients see exactly what GLPFlo will charge
  // the processor BEFORE they tap Pay. Fetches /api/checkout/quote
  // whenever the medication or tier changes (or a code is applied).
  const [quote, setQuote] = useState(null);
  const loadQuote = useCallback(async (med, tier) => {
    if (!med) { setQuote(null); return; }
    try {
      const { data } = await API.post("/checkout/quote", {
        medication_id: med.id,
        payment_type: tier || "one_time",
      });
      setQuote(data);
    } catch { setQuote(null); }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [medRes, qRes, subRes, pricingRes] = await Promise.all([
        API.get("/medications"),
        API.get("/questionnaires/mine"),
        API.get("/subscription-info").catch(() => ({ data: { discount_percent: 10 } })),
        // /api/pricing is the single source of truth for tier prices used
        // by both the Pricing tab AND the mobile app. Use it here so the
        // /medications checkout can't drift from the marketing prices.
        API.get("/pricing").catch(() => ({ data: [] })),
      ]);
      setMedications(medRes.data);
      setMyQuestionnaires(qRes.data);
      setSubDiscount(subRes.data.discount_percent);
      // Map drug_slug -> { monthly_price, solo_subscribe_price, royal_tier_price }
      const map = {};
      for (const row of pricingRes.data || []) {
        if (!row?.drug_slug) continue;
        map[row.drug_slug] = {
          monthly_price: Number(row.monthly_price) || 0,
          solo_subscribe_price: Number(row.solo_subscribe_price ?? row.monthly_subscribe_price) || 0,
          royal_tier_price: Number(row.royal_tier_price) || 0,
        };
      }
      setPricingTiers(map);
      if (!chosenQId && qRes.data.length > 0) setChosenQId(qRes.data[0].id);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [chosenQId]);

  useEffect(() => { loadData(); loadDiscounts(); }, [loadData, loadDiscounts]);
  useEffect(() => { loadQuote(selectedMed, paymentType); }, [selectedMed, paymentType, loadQuote]);

  const redeemPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code || !selectedMed) {
      if (!selectedMed) toast.error("Pick a medication first");
      return;
    }
    setRedeeming(true);
    try {
      // Processor-agnostic apply-code: redeems the code + returns the
      // fresh quote (base, discount_total, final_amount, applied_coupons)
      // in one round-trip so the price breakdown updates immediately.
      const { data } = await API.post("/checkout/apply-code", {
        code,
        medication_id: selectedMed.id,
        payment_type: paymentType || "one_time",
      });
      if (data.applied) {
        toast.success(data.message || `Promo ${code} applied — $${(data.quote?.final_amount || 0).toFixed(2)} after discounts.`);
      } else {
        toast(data.message || "Code redeemed but a bigger discount is already applied.", { duration: 5000 });
      }
      setPromoCode("");
      loadDiscounts();
      loadQuote(selectedMed, paymentType);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  // PRICING MATH (post 2026-06 flat-pricing switch — matches Square backend):
  //   solo_subscribe (1-Month Subscribe) = basePrice - $4
  //   royal_90 (3-Month Subscribe)       = (basePrice - $14) per month, billed x3 every 3 months
  // Wherever `pricingTiers[slug]` is populated from /api/pricing we prefer
  // those authoritative values so this page never drifts from the Pricing
  // tab or the mobile app. The `- 4` / `- 14` fallback only kicks in for
  // brand-new drugs that aren't in `pricing_tiers` yet.
  const SOLO_SUBSCRIBE_OFF = 4;
  const ROYAL_PER_MONTH_OFF = 14;

  // Resolve /api/pricing tier row for a given medication. Matches on
  // drug slug via medication.id prefix or name prefix.
  const tiersFor = useCallback((med) => {
    if (!med) return null;
    const idLower = String(med.id || "").toLowerCase();
    const nameLower = String(med.name || "").toLowerCase();
    for (const [slug, tiers] of Object.entries(pricingTiers)) {
      if (idLower.startsWith(slug) || nameLower.startsWith(slug)) return tiers;
    }
    return null;
  }, [pricingTiers]);

  const getPrice = (basePrice, med) => {
    const tiers = tiersFor(med);
    if (paymentType === "solo_subscribe") {
      const p = tiers?.solo_subscribe_price;
      return (p != null && p > 0 ? p : Math.max(0, basePrice - SOLO_SUBSCRIBE_OFF)).toFixed(2);
    }
    if (paymentType === "royal_90") {
      const perMonth = tiers?.royal_tier_price;
      const pm = perMonth != null && perMonth > 0 ? perMonth : Math.max(0, basePrice - ROYAL_PER_MONTH_OFF);
      return (pm * 3).toFixed(2);
    }
    const p = tiers?.monthly_price;
    return (p != null && p > 0 ? p : basePrice).toFixed(2);
  };

  const getPerMonthPrice = (basePrice, med) => {
    const tiers = tiersFor(med);
    if (paymentType === "solo_subscribe") {
      const p = tiers?.solo_subscribe_price;
      return (p != null && p > 0 ? p : Math.max(0, basePrice - SOLO_SUBSCRIBE_OFF)).toFixed(2);
    }
    if (paymentType === "royal_90") {
      const p = tiers?.royal_tier_price;
      return (p != null && p > 0 ? p : Math.max(0, basePrice - ROYAL_PER_MONTH_OFF)).toFixed(2);
    }
    return null;
  };

  const handleOrderAndPay = async () => {
    if (!selectedMed) { toast.error("Please select a medication"); return; }
    if (!chosenQId) { toast.error("Please complete a questionnaire first"); navigate("/questionnaire"); return; }
    if (!termsAccepted) {
      toast.error("Please confirm you've read the Terms of Service before continuing.");
      // Scroll the T&C card into view + animate flash
      const card = document.querySelector('[data-testid="terms-ack-card"]');
      if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    // Hoist the created order out of the try block scope — the Intuit
    // error handler in the catch needs `order?.id` to open the card modal
    // with the already-created order, and `const` in a try is NOT visible
    // in the matching catch.
    let order = null;
    try {
      // Audit-log the acknowledgement BEFORE we kick off the order so a refund
      // dispute can always be tied to a server-side click record.
      await API.post("/orders/terms-acknowledged", {
        version: "2026-04-25",
        medication_id: selectedMed.id,
        payment_type: paymentType,
      }).catch(() => {});
      // The 2 auto-renew tiers use NATIVE Square subscriptions: after the
      // order is created we open a secure card-on-file modal (Square Web
      // Payments SDK) and start a recurring subscription. One-time tiers use
      // the Square-hosted Checkout redirect.
      const isRecurring = paymentType === "solo_subscribe" || paymentType === "royal_90";
      const _created = await API.post("/orders", {
        medication_id: selectedMed.id,
        questionnaire_id: chosenQId,
        pharmacy_tenant_id: null,
        payment_type: paymentType,
        // Starting strength is no longer patient-selected — the prescriber
        // determines the dose during medical review.
        requested_strength: null,
        dose_request_above_starter: false,
        dose_request_acknowledged: false,
        above_starter_documentation: null,
      });
      order = _created.data;

      if (isRecurring) {
        try {
          const { analytics } = await import("@/instrument");
          analytics.capture("checkout_started", {
            medication_slug: selectedMed.id, dose_tier: paymentType, variant: "subscription",
            has_coupon: Boolean(discountInfo?.coupon_id),
          });
        } catch (_e) { /* noop */ }
        const perMonth = Number(selectedMed.price) || 0;
        const amount = paymentType === "royal_90"
          ? Math.max(0, (perMonth - 14) * 3)
          : Math.max(0, perMonth - 4);
        setSubscribeCtx({
          medicationId: selectedMed.id,
          paymentType,
          orderId: order.id,
          tierName: `${selectedMed.name} — ${paymentType === "royal_90" ? "3-Month Subscribe" : "1-Month Subscribe"}`,
          amount,
          cadence: paymentType === "royal_90" ? "QUARTERLY" : "MONTHLY",
          billingDefaults: patientProfile,
        });
        setSubmitting(false);
        return;
      }

      const { executeCaptcha } = await import("@/lib/recaptcha");
      const captcha_token = await executeCaptcha("checkout").catch(() => null);
      const { data: checkout } = await API.post("/payments/checkout", {
        medication_id: selectedMed.id,
        order_id: order.id,
        origin_url: window.location.origin,
        payment_type: paymentType,
        coupon_id: discountInfo?.coupon_id || null,
        resubmit_from_order_id: denialCredit?.eligible ? resubmitContext?.original_order_id : null,
        captcha_token,
        captcha_version: "v3",
      });
      if (checkout.url) {
        try {
          const { analytics } = await import("@/instrument");
          analytics.capture("checkout_started", {
            medication_slug: selectedMed.id,
            dose_tier: paymentType,
            variant: "standard",
            has_coupon: Boolean(discountInfo?.coupon_id),
          });
        } catch (_e) { /* noop */ }
        // Redirect to Square's hosted, GLPFlo-branded checkout page. We never
        // touch card data — Square handles the card entry on its domain and
        // returns the patient to /payment/success?orderId=...
        window.location.href = checkout.url;
      } else {
        toast.error("Payment redirect failed");
      }
    } catch (e) {
      // Required HIPAA / state-telemedicine consents not yet signed → open modal,
      // patient signs both, we transparently retry the same checkout flow.
      // NOTE: the shared axios interceptor (lib/api.js) flattens structured
      // `detail` objects into a string and preserves the original object on
      // `data._detail`. Read the structured object so we can branch on `code`.
      const respData = e?.response?.data || {};
      const detail = (respData._detail !== undefined && respData._detail !== null)
        ? respData._detail
        : respData.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (e?.response?.status === 409 && code === "consents_required") {
        setShowConsentModal(true);
        setPendingPayAfterConsent(true);
        setSubmitting(false);
        return;
      }
      // Duplicate active medication — patient already on this med. Show a
      // friendly modal with two clear next steps instead of a red toast so
      // the user understands they're not blocked from care, just from
      // double-ordering.
      if (e?.response?.status === 409 && code === "duplicate_active_medication") {
        setDuplicateMedInfo(detail);
        setSubmitting(false);
        return;
      }
      // Identity-proofing not complete — the gate component should have
      // caught this on page mount, but a stale state or a direct API
      // bypass attempt will land here. Re-open the gate.
      if (e?.response?.status === 412 && code === "id_verification_required") {
        toast.error(detail?.message || "Please verify your identity first");
        setIdStatus({
          verified: false,
          verification_required: true,
          stripe_status: detail?.stripe_status || null,
        });
        setSubmitting(false);
        return;
      }
      // No shipping address on profile — HARD STOP. Patient MUST self-enter
      // their address in Profile before any charge. Route them there and
      // show WHICH fields are missing so they fix it in one visit.
      if (e?.response?.status === 412 && code === "address_required") {
        const miss = (detail?.missing_fields || []).map((f) => f.replace("address_", "")).join(", ");
        toast.error(
          miss
            ? `Add your shipping address first (missing: ${miss}).`
            : "Please add your shipping address in Profile before checking out."
        );
        setSubmitting(false);
        setTimeout(() => {
          navigate(detail?.profile_route || "/profile?from=address_required");
        }, 1200);
        return;
      }
      // Admin flipped the active processor to Intuit — open the Intuit
      // card modal with the same checkout params instead of showing an
      // error. Server-side tokenization keeps PAN/CVV out of our DB.
      const _basePrice = Number(selectedMed?.price) || 0;
      const _amount = paymentType === "royal_90"
        ? Math.max(0.5, (_basePrice - 14) * 3)
        : paymentType === "solo_subscribe"
          ? Math.max(0.5, _basePrice - 4)
          : _basePrice;
      const intuitBranch = intuitCtxFromCheckoutError(e, {
        medication_id: selectedMed.id,
        payment_type: paymentType,
        order_id: order?.id,
        coupon_id: discountInfo?.coupon_id || null,
        resubmit_from_order_id: denialCredit?.eligible ? resubmitContext?.original_order_id : null,
        amount: _amount,
      });
      if (intuitBranch) {
        setIntuitCtx(intuitBranch);
        setSubmitting(false);
        return;
      }
      // Surface the actual server reason (orders paused, medication unavailable,
      // Stripe config error, etc.) so the patient knows WHY — generic toasts
      // are useless for production support.
      const status = e?.response?.status;
      const detailMsg =
        typeof detail === "string" ? detail
        : (detail && typeof detail === "object" && detail.message) ? detail.message
        : null;
      // Processor/Stripe config errors leak raw gateway text which is useless
      // to a patient. Detect them so we can show a friendly message instead.
      const looksLikeProcessorConfig =
        /payment processor.*not.*config|Auth\.?Net|Authorize\.?Net|Invalid.*Login|Hosted.*token|Stripe/i.test(detailMsg || "");
      const looksLikeStripeConfig = looksLikeProcessorConfig;
      const fallbackByStatus = {
        503: "Online ordering is paused right now — please try again shortly or contact customer.service@glpflo.com.",
        409: "Something about your account needs attention before we can finish the order. Please reach out via chat or customer.service@glpflo.com.",
        404: "That medication isn't available right now — please pick a different option or contact support.",
        401: "Your session expired — please sign in again and retry.",
        403: "Your account isn't permitted to place this order. Please contact customer.service@glpflo.com.",
        429: "Too many attempts in a row — please wait a minute and try again.",
        502: "Our payment processor is unreachable right now. Please retry shortly.",
        500: "Our checkout had a hiccup. Please retry; if it persists, contact customer.service@glpflo.com.",
      };
      const friendly = looksLikeStripeConfig
        ? "Our payment system is being updated and can't accept charges right now. Please try again in a few minutes or email customer.service@glpflo.com so we can finish your order."
        : null;
      const msg = friendly || detailMsg || fallbackByStatus[status] || "Failed to process order — please try again or contact customer.service@glpflo.com.";
      toast.error(msg);
      console.error("Order/checkout failed:", { status, detail, error: e });
      try {
        const { analytics } = await import("@/instrument");
        analytics.capture("checkout_failed", {
          medication_slug: selectedMed?.id,
          dose_tier: paymentType,
          status: status || 0,
          reason: (detailMsg || "unknown").slice(0, 120),
        });
      } catch (_e) { /* noop */ }
    } finally { setSubmitting(false); }
  };

  const onConsentsComplete = () => {
    setShowConsentModal(false);
    if (pendingPayAfterConsent) {
      setPendingPayAfterConsent(false);
      // Retry the same flow now that both consents are signed.
      handleOrderAndPay();
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading medications...</div>;

  // Identity-proofing blocker. Render the upload UI in place of the medication
  // picker until the patient has both ID + video on file. Server enforces
  // the same check at POST /orders (412) so this can't be bypassed.
  if (idStatusLoaded && !idReady) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="id-verification-gate">
        <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
            </Link>
            <Button variant="ghost" className="rounded-full text-slate-600" onClick={() => navigate("/dashboard")} data-testid="gate-back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <div className="rounded-3xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 p-5 sm:p-7 mb-6">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-purple-600/10 p-3">
                <ShieldAlert className="w-6 h-6 text-purple-700" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Upload your ID to continue
                </h1>
                <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                  State telemedicine rules require your government-issued ID on file before a prescription can be issued. Just
                  <strong> upload a photo of your driver&apos;s license, state ID, or passport </strong>
                  and enter the details — you&apos;ll be able to pick your medication and complete checkout as soon as it&apos;s uploaded. Your prescriber verifies the ID during prescription review, so nothing is held up while you wait.
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  <span data-testid="gate-status-identity"
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 border ${
                          idStatus?.verified
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : idStatus?.has_id_card
                              ? "border-amber-300 bg-amber-50 text-amber-800"
                              : "border-rose-300 bg-rose-50 text-rose-800"
                        }`}>
                    {idStatus?.verified ? "✅ Identity verified"
                      : idStatus?.has_id_card ? "⏳ ID pending review"
                      : "① Identity check required"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <IdGateWhyCard status={idStatus} />

          <IdVerificationCard onStatusChange={(s) => setIdStatus(s)} />

          <p className="mt-6 text-center text-xs text-slate-500">
            Once your ID is uploaded, the medication picker unlocks automatically — your prescriber verifies the ID during prescription review.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="medication-selection-page">
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Button variant="ghost" className="rounded-full text-slate-600" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {resubmitContext && (
          <div
            className="mb-6 rounded-2xl border-2 border-purple-300 bg-purple-50 dark:bg-purple-950/30 dark:border-purple-800 p-4 sm:p-5 flex items-start gap-3"
            data-testid="resubmit-banner"
          >
            <div className="w-9 h-9 rounded-xl bg-purple-200 dark:bg-purple-900 text-purple-700 dark:text-purple-200 flex items-center justify-center flex-shrink-0 font-bold">↻</div>
            <div className="text-sm leading-relaxed">
              <p className="font-bold text-purple-900 dark:text-purple-100">Welcome back — we&rsquo;ve pre-filled your previous order.</p>
              <p className="text-purple-800 dark:text-purple-200 mt-0.5">
                {resubmitContext.reason === "insufficient_documentation"
                  ? <>Your prescriber asked for more detail on your prior GLP-1 use. Scroll down to the dose-request card and fill in the four required fields with as much specificity as you can.</>
                  : <>Your previous order was denied. Update your selections below and resubmit when ready.</>}
              </p>
              <p className="text-[12px] text-purple-700 dark:text-purple-300 mt-1.5 opacity-80">
                {denialCredit?.eligible
                  ? <>Your <b>${Number(denialCredit.credit_amount).toFixed(2)}</b> consultation-fee credit from the prior denial will be applied automatically at checkout.</>
                  : denialCredit && !denialCredit.eligible && denialCredit.reason === "credit_window_expired"
                    ? <>The 30-day window for your prior denial credit has expired, but you can still resubmit your order.</>
                    : denialCredit?.reason === "already_credited"
                      ? <>Your prior denial credit has already been applied.</>
                      : <>Your $25 consultation fee from the prior denial will be credited toward this re-order.</>}
              </p>
            </div>
          </div>
        )}
        {/* First-time patient nudge — routes new visitors to the $79.99
            Starter Dose landing offer on /pricing so they don't miss the
            promo when browsing full-price plans here. */}
        <div
          className="mb-6 rounded-2xl px-4 sm:px-5 py-3.5 flex items-center gap-3 flex-wrap shadow-lg shadow-emerald-900/25 text-white"
          style={{ background: "linear-gradient(135deg,#059669,#0891b2)" }}
          data-testid="intake-starter-dose-banner"
        >
          <div className="flex-1 min-w-[220px]">
            <p className="font-extrabold text-[15px] leading-tight">First-time patient? Get your Starter Dose for $79.99</p>
            <p className="text-xs opacity-90 mt-0.5 font-medium">Semaglutide or Tirzepatide — starter dose, one-month supply, no subscription.</p>
          </div>
          <Link
            to="/pricing#new-patient-deal-card-banner"
            className="bg-white text-emerald-800 px-4 py-2 rounded-full font-extrabold text-[13px] whitespace-nowrap hover:scale-[1.04] transition-transform"
            data-testid="intake-starter-dose-cta"
          >
            See the $79.99 offer →
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Select Your Medication</h1>
        <p className="text-slate-600 mb-8">Choose from our physician-prescribed GLP-1 medications compounded with B12.</p>

        {/* Medication Cards */}
        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          {uniqueMeds.map((med) => {
            const baseName = (med.name || "").split(/\s+with\s+/i)[0].trim();
            return (
            <div key={med.id}
              className={`group relative text-left bg-white rounded-2xl border-2 overflow-hidden transition-all duration-500 ease-out hover:shadow-2xl hover:shadow-purple-200/60 hover:-translate-y-1 ${selectedMed?.id === med.id ? "border-purple-500 shadow-[0_0_40px_-10px_rgba(147,51,234,0.15)]" : "border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`}
              data-testid={`med-card-${med.id}`}>
              {/* Inline "learn more" tap-target — opens the same illuminated
                  modal used on /browse-medications so the experience is
                  consistent for logged-in patients. */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setInfoMed(med); }}
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-[11px] font-semibold text-purple-600 hover:text-purple-800 bg-white/90 hover:bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 transition shadow-sm"
                aria-label={`Learn more about ${baseName}`}
                data-testid={`med-info-${med.id}`}
              >
                <Info className="w-3 h-3" /> Learn more
              </button>
              <button
                type="button"
                onClick={() => setSelectedMed(med)}
                className="w-full text-left"
                aria-label={`Select ${baseName}`}
                data-testid={`med-select-${med.id}`}
              >
                {/* Themed mock vial — matches /browse-medications visual; purely
                    decorative, NOT a photo of the dispensed product. */}
                <div className="relative h-36 bg-gradient-to-b from-purple-50 via-violet-50 to-white flex items-center justify-center pt-3 pb-1">
                  <VialIllustration variant={vialVariantFor(baseName)} />
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider font-semibold text-purple-400/80 bg-white/70 backdrop-blur rounded-full px-2 py-0.5">
                    Illustration
                  </span>
                </div>
                <div className="p-6 pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-purple-600">{med.type}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{baseName}</h3>
                  <p className="text-sm text-slate-500 mb-3">{med.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Strength determined by your prescriber</span>
                    <Link
                      to="/pricing"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs font-semibold text-purple-600 hover:text-purple-800 inline-flex items-center gap-1"
                      data-testid={`med-card-pricing-link-${med.id}`}
                    >
                      View tiers & pricing <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  {selectedMed?.id === med.id && (
                    <div className="flex items-center gap-1 mt-3 text-purple-600 text-sm font-medium"><CheckCircle className="w-4 h-4" /> Selected</div>
                  )}
                </div>
              </button>
            </div>
            );
          })}
        </div>

        {/* Illustration disclaimer — the marketing mock vial is not a photo of
            the actual dispensed product. Real vials are labelled by the
            licensed compounding pharmacy, not GLPFlo. */}
        <p className="-mt-6 mb-10 text-center text-[11px] text-slate-400 italic max-w-2xl mx-auto leading-relaxed" data-testid="vial-illustration-disclaimer">
          Vial images shown are stylised illustrations for reference only.
          Your actual medication will arrive in packaging branded by the
          licensed US compounding pharmacy that fills your prescription,
          not GLPFlo.
        </p>

        {selectedMed && (
          <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8" data-testid="order-section">
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Choose Your Plan</h2>
            <p className="text-sm text-slate-500 mb-4">Select a payment tier for <strong>{selectedMed.name}</strong>. Your provider will determine the specific strength.</p>

            <RateLockBadge className="mb-6" testid="rate-lock-checkout" />

            {/* Payment Tiers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="payment-tiers">
              {Object.entries(TIER_INFO).map(([key, tier]) => {
                const TierIcon = tier.icon;
                const isActive = paymentType === key;
                // Calculate price for THIS tier specifically — prefer the
                // /api/pricing row so this page can never disagree with the
                // Pricing tab / mobile app.
                const tiers = tiersFor(selectedMed);
                let tierPrice;
                if (key === "one_time") {
                  tierPrice = (tiers?.monthly_price || selectedMed.price).toFixed(2);
                } else if (key === "solo_subscribe") {
                  const p = tiers?.solo_subscribe_price;
                  tierPrice = (p != null && p > 0 ? p : Math.max(0, selectedMed.price - 4)).toFixed(2);
                } else {
                  const perMonthRoyal = tiers?.royal_tier_price != null && tiers.royal_tier_price > 0
                    ? tiers.royal_tier_price
                    : Math.max(0, selectedMed.price - 14);
                  tierPrice = (perMonthRoyal * 3).toFixed(2);
                }
                const perMonth = key === "royal_90"
                  ? (tiers?.royal_tier_price != null && tiers.royal_tier_price > 0
                      ? tiers.royal_tier_price
                      : Math.max(0, selectedMed.price - 14)).toFixed(2)
                  : null;

                const tierIconActiveColor =
                  key === "royal_90" ? "text-amber-600"
                  : key === "solo_subscribe" ? "text-sky-600"
                  : "text-purple-600";
                const perMonthColor = "text-amber-600";
                return (
                  <button key={key} onClick={() => {
                      setPaymentType(key);
                      if (key === "royal_90") setRoyalBurstKey(k => k + 1);
                    }}
                    className={`relative rounded-xl border-2 p-5 text-left transition-all duration-200 ${isActive ? tier.activeColor : tier.color}`}
                    data-testid={`payment-tier-${key}`}>
                    {key === "royal_90" && (
                      <TierCelebration preset="royal" burstKey={royalBurstKey} active={isActive} labelOverride={celebrationLabels.royal} />
                    )}
                    {tier.badge && (
                      <span className={`absolute -top-2.5 right-3 ${tier.badgeColor} text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full`}>
                        {tier.badge}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <TierIcon className={`w-5 h-5 ${isActive ? tierIconActiveColor : "text-slate-400"}`} />
                      <span className="font-semibold text-sm text-slate-900">{tier.label}</span>
                      <InfoBubble text={tier.detail} />
                    </div>
                    <p className="text-xs text-slate-500 mb-3 leading-relaxed">{tier.desc}</p>
                    <div>
                      <span className="text-xl font-bold text-purple-600">${tierPrice}</span>
                      <span className="text-xs text-slate-400 ml-1">
                        {key === "one_time"
                          ? "/one-time"
                          : key === "solo_subscribe"
                            ? "/28 days"
                            : "/3 months"}
                      </span>
                    </div>
                    {perMonth && (
                      <p className={`text-xs ${perMonthColor} font-medium mt-1`}>${perMonth}/month equivalent</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2 leading-tight italic">
                      {key === "royal_90"
                        ? "Auto-billed every 3 months (84 days). A new prescription is reviewed before each cycle."
                        : key === "solo_subscribe"
                          ? "Auto-billed every 28 days. A new prescription is reviewed before each cycle."
                          : "A new prescription is required each month."}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Questionnaire Selection & Order */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Selected Medication</p>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <p className="font-semibold text-slate-900">{selectedMed.name}</p>
                  <p className="text-sm text-slate-600">{selectedMed.dosage}</p>
                  <p className="text-xs text-slate-400 mt-1">Strength determined by your prescriber</p>
                </div>
              </div>
              <div>
                {myQuestionnaires.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Questionnaire</p>
                    <Select value={chosenQId} onValueChange={setChosenQId}>
                      <SelectTrigger className="rounded-xl" data-testid="questionnaire-select"><SelectValue placeholder="Select questionnaire" /></SelectTrigger>
                      <SelectContent>
                        {myQuestionnaires.map((q) => (
                          <SelectItem key={q.id} value={q.id}>Submitted {new Date(q.submitted_at).toLocaleDateString()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100">
              {/* Butterfly clipboard auto-detect — appears only when the
                  visitor has a `BFLY-XXXX-XXXX` code on their clipboard
                  (typically because they just caught the easter egg). */}
              {showBflyPill && (
                <div
                  className="mb-4 rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-pink-50 px-4 py-3 flex items-center gap-3 shadow-sm"
                  data-testid="butterfly-clipboard-pill"
                >
                  <span className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white flex items-center justify-center shadow">
                    <Sparkles className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                      Butterfly code detected on clipboard
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5 font-mono">
                      {clipboardBfly} <span className="font-sans">— apply for $7 off?</span>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={applyBflyFromClipboard}
                    disabled={redeeming}
                    className="rounded-full bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
                    data-testid="butterfly-clipboard-apply"
                  >
                    {redeeming ? "Applying…" : "Apply code"}
                  </Button>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setBflyDismissed(true)}
                    className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-slate-200/70 inline-flex items-center justify-center text-slate-400 hover:text-slate-600"
                    data-testid="butterfly-clipboard-dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {/* Promo Code Input */}
              <div className="mb-4 flex gap-2" data-testid="promo-code-section">
                <input
                  type="text"
                  placeholder="Have a promo code?"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && redeemPromo()}
                  className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm uppercase tracking-wider font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="promo-code-input"
                />
                <Button onClick={redeemPromo} disabled={redeeming || !promoCode.trim()} variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="redeem-promo-btn">
                  {redeeming ? "Redeeming..." : "Apply"}
                </Button>
              </div>

              {/* Discount Banner */}
              {discountInfo && discountInfo.total_discount > 0 && (
                <div className="mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4 animate-pulse-once" data-testid="discount-banner">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Tag className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-emerald-800 text-sm">
                          {discountInfo.coupon_label || "Discount applied"}
                        </p>
                        <p className="text-xs text-emerald-600">Applied automatically at checkout</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-emerald-600">-${discountInfo.total_discount.toFixed(2)}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-emerald-700 mt-2 leading-snug" data-testid="discount-explainer">
                    You always get the best available deal. When a promo isn&apos;t stackable, we automatically pick whichever
                    saves you the most — so no coupon of yours is ever lost or wasted.
                  </p>
                </div>
              )}

              {/* Itemized live breakdown — base × each code × credit × final.
                  Patient sees the exact cents GLPFlo will send to the payment
                  processor BEFORE tapping Pay. Updates live as codes are
                  applied/removed. */}
              {quote && (quote.applied_coupons?.length > 0 || quote.credit_applied > 0) && (
                <div className="mb-4 bg-white border-2 border-purple-200 rounded-xl p-4" data-testid="checkout-breakdown">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Price breakdown</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Base · {quote.med_name}</span>
                      <span className="font-mono text-slate-800">${(quote.base_amount + (quote.credit_applied || 0)).toFixed(2)}</span>
                    </div>
                    {quote.credit_applied > 0 && (
                      <div className="flex items-center justify-between text-purple-700">
                        <span>Denial consultation credit</span>
                        <span className="font-mono">-${quote.credit_applied.toFixed(2)}</span>
                      </div>
                    )}
                    {(quote.applied_coupons || []).map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-emerald-700" data-testid={`breakdown-coupon-${c.id}`}>
                        <span>{c.label || c.code || "Discount"}{c.code ? ` · ${c.code}` : ""}</span>
                        <span className="font-mono">-${Number(c.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
                      <span className="font-bold text-slate-900">You pay today</span>
                      <span className="font-bold text-xl text-purple-700 font-mono" data-testid="breakdown-final">${quote.final_amount.toFixed(2)}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 pt-1">
                      This is the exact amount the payment processor will charge. Receipt breakdown stays itemized.
                    </p>
                  </div>
                </div>
              )}

              {/* Denial-credit line — applied server-side at checkout when the
                  patient resubmitted from a denial email within 30 days. */}
              {denialCredit?.eligible && (
                <div
                  className="mb-4 bg-gradient-to-r from-purple-50 to-fuchsia-50 border-2 border-purple-200 rounded-xl p-4"
                  data-testid="denial-credit-banner"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">↻</div>
                      <div>
                        <p className="font-bold text-purple-800 text-sm">Denial consultation credit</p>
                        <p className="text-xs text-purple-600">Applied automatically at checkout — your previous $25 consult fee, refunded</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-purple-600" data-testid="denial-credit-amount">-${Number(denialCredit.credit_amount).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* What's in the box — included with every order, no extra charge */}
              <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-900/60 p-4" data-testid="whats-in-the-box">
                <p className="text-xs uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 mb-2 inline-flex items-center gap-1.5">
                  <PackageCheck className="w-3.5 h-3.5" /> What&apos;s in the box
                </p>
                <ul className="text-xs text-slate-700 dark:text-slate-200 space-y-1">
                  <li className="flex gap-1.5"><span className="text-emerald-600">✓</span> Your prescribed vial(s) compounded by our partner pharmacy</li>
                  <li className="flex gap-1.5"><span className="text-emerald-600">✓</span> Enough <b>insulin-grade syringes</b> for the full supply</li>
                  <li className="flex gap-1.5"><span className="text-emerald-600">✓</span> <b>Alcohol prep pads</b> — one per injection plus extras</li>
                  <li className="flex gap-1.5"><span className="text-emerald-600">✓</span> Printed <b>&quot;How to inject + vial storage&quot;</b> info pamphlet</li>
                  <li className="flex gap-1.5"><span className="text-emerald-600">✓</span> Tracked, temperature-protected shipping</li>
                </ul>
              </div>

              {/* Terms acknowledgment — required before Pay button enables */}
              <div className="mb-4">
                <TermsAcknowledgmentCard checked={termsAccepted} onChange={setTermsAccepted} />
              </div>

              {/* Pre-flight pharmacy shipping coverage check — surfaces a red
                  banner BEFORE submit if the patient's state isn't currently
                  served by any of our active pharmacy partners. Mirrors the
                  backend can-ship guard so we don't take payment for an order
                  the prescriber can never route. */}
              <div className="mb-4" data-testid="shipping-coverage-row">
                <ShippingCoverageBadge
                  patientState={patientState}
                  patientEmail={patientEmail}
                  onUnsupportedChange={setShippingUnsupported}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CreditCard className="w-4 h-4" />
                  <span>Secure payment via Square</span>
                </div>
                <div className="flex items-center gap-4">
                  {discountInfo && discountInfo.total_discount > 0 && selectedMed && (
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-slate-400 line-through">${getPrice(selectedMed.price, selectedMed)}</p>
                      <p className="text-lg font-bold text-emerald-600">${Math.max(getPrice(selectedMed.price, selectedMed) - discountInfo.total_discount, 0.50).toFixed(2)}</p>
                    </div>
                  )}
                  <Button
                    onClick={handleOrderAndPay}
                    disabled={submitting || !termsAccepted || shippingUnsupported}
                    title={shippingUnsupported ? "We can't ship to your state yet — update your shipping address to continue." : undefined}
                    className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-order-btn"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>
                      Pay ${discountInfo && discountInfo.total_discount > 0 && selectedMed ? Math.max(getPrice(selectedMed.price, selectedMed) - discountInfo.total_discount, 0.50).toFixed(2) : selectedMed ? getPrice(selectedMed.price, selectedMed) : "0"} & Submit <ArrowRight className="w-4 h-4 ml-2" />
                    </>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <PublicFooter />
      <ConsentFlowModal
        open={showConsentModal}
        onComplete={onConsentsComplete}
        onCancel={() => { setShowConsentModal(false); setPendingPayAfterConsent(false); }}
      />
      <MedicationInfoModal med={infoMed} onClose={() => setInfoMed(null)} />

      <SquareSubscribeModal
        open={!!subscribeCtx}
        onClose={() => { setSubscribeCtx(null); navigate("/dashboard"); }}
        medicationId={subscribeCtx?.medicationId}
        paymentType={subscribeCtx?.paymentType}
        orderId={subscribeCtx?.orderId}
        tierName={subscribeCtx?.tierName}
        amount={subscribeCtx?.amount}
        cadence={subscribeCtx?.cadence}
        billingDefaults={subscribeCtx?.billingDefaults}
      />

      <IntuitCheckoutModal
        open={!!intuitCtx}
        onOpenChange={(open) => { if (!open) setIntuitCtx(null); }}
        ctx={intuitCtx}
      />

      {/* Duplicate-active-medication notice */}
      {duplicateMedInfo && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          data-testid="duplicate-med-modal"
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-3">
              <span className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </span>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  You&apos;re already on this medication
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                  {duplicateMedInfo.message}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  navigate(`/dashboard?order=${encodeURIComponent(duplicateMedInfo.existing_order_id || "")}`);
                }}
                className="w-full rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2.5"
                data-testid="duplicate-med-view-order"
              >
                View my current order
              </button>
              <button
                type="button"
                onClick={() => {
                  navigate("/review-change-request");
                }}
                className="w-full rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 text-sm font-semibold px-4 py-2.5"
                data-testid="duplicate-med-request-change"
              >
                Request a strength change
              </button>
              <button
                type="button"
                onClick={() => setDuplicateMedInfo(null)}
                className="w-full text-xs text-slate-500 hover:text-slate-700 mt-1"
                data-testid="duplicate-med-close"
              >
                I&apos;ll wait for my next refill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
